import { useAuth } from "context/auth/auth";
import { useLayout } from "context/layout/layout";
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import logo from "../../../assets/white.png";
import { PasswordField } from "components/PasswordField";
import { useIsMobile } from "hooks/useIsMobile";
import { isValidEmail } from "utils/validations";

export function PortalLogin() {
  if (localStorage.getItem("userdata:portal"))
    window.location.replace("/portal/dashboard");

  const auth = useAuth();
  const layout = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  // Si llegan acá redirigidos por sesión expirada, avisar.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("expired") === "1") {
      layout.errorAlert({ message: "Tu sesión expiró. Inicia sesión nuevamente." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleChange = ({ target: { value, name } }) => {
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    if (!form.email || !form.password) {
      return layout.errorAlert({ message: "Todos los campos son obligatorios" });
    }
    if (!isValidEmail(form.email)) {
      return layout.errorAlert({ message: "Email inválido" });
    }

    setLoading(true);
    try {
      const result = await auth.requestPortalAuthentication({ credentials: form });
      if (result) {
        layout.successAlert({ message: "Inicio de sesion exitoso" });
        setTimeout(() => navigate("/portal/dashboard"), 1500);
      }
    } catch (err) {
      const status = err?.response?.status;
      // Mensaje genérico para evitar enumeración de usuarios. Solo se
      // diferencia el caso de servidor caído (5xx) o sin red.
      const msg =
        status && status >= 500
          ? "El servicio no está disponible. Intenta nuevamente en unos minutos."
          : "Email o contraseña inválidos";
      layout.errorAlert({ message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.95rem",
    border: "1.5px solid #e2e8f0",
    borderRadius: "0.625rem",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    background: "#fff",
    color: "#1e293b",
    boxSizing: "border-box",
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = "#0353a4";
    e.target.style.boxShadow = "0 0 0 3px rgba(3,83,164,0.1)";
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "100vh", width: "100%", overflow: "auto" }}>
      {/* Left panel - branding (oculto en mobile, header compacto) */}
      <div
        style={{
          width: isMobile ? "100%" : "50%",
          display: "flex",
          flexDirection: "column",
          justifyContent: isMobile ? "flex-start" : "center",
          padding: isMobile ? "1.5rem 1.25rem" : "0 4rem",
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #334155 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-120px",
            right: "-120px",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-200px",
            left: "-100px",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.04)",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "480px" }}>
          <img
            src={logo}
            alt="Profit"
            style={{ width: isMobile ? "120px" : "160px", marginBottom: isMobile ? "0.75rem" : "2.5rem", opacity: 0.95 }}
          />

          {!isMobile && (
            <>
              <h1
                style={{
                  color: "#fff",
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: "1rem",
                  letterSpacing: "-0.02em",
                }}
              >
                Portal de Clientes
                <br />
                <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400 }}>
                  Profit Accounting
                </span>
              </h1>

              <p
                style={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                  marginBottom: "2.5rem",
                }}
              >
                Accede a tus registros de compra y venta del SII,
                documentos tributarios y reportes contables en un solo lugar.
              </p>

              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                {["Registro de Compras", "Registro de Ventas", "Documentos"].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.5rem 0.875rem",
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.07)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <span
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                      }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right panel - form */}
      <div
        style={{
          width: isMobile ? "100%" : "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
          position: "relative",
          padding: isMobile ? "2rem 0" : 0,
        }}
      >
        <div style={{ width: "100%", maxWidth: "400px", padding: "0 2rem" }}>
          <div style={{ marginBottom: "2rem" }}>
            <p
              style={{
                fontSize: "0.75rem",
                color: "#94a3b8",
                fontWeight: 500,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: "0.5rem",
              }}
            >
              Portal de clientes
            </p>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: "#0f172a",
                marginBottom: "0.25rem",
              }}
            >
              Iniciar sesion
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#64748b" }}>
              Ingresa tus credenciales para acceder a tu portal
            </p>
          </div>

          <form autoComplete="on" onSubmit={(e) => e.preventDefault()}>
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "0.5rem",
                }}
              >
                Correo electronico
              </label>
              <input
                type="email"
                placeholder="nombre@empresa.cl"
                value={form.email}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoComplete="email"
                name="email"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label
                htmlFor="portal-login-password"
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "0.5rem",
                }}
              >
                Contraseña
              </label>
              <PasswordField
                value={form.password}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                name="password"
                placeholder="Ingresa tu contraseña"
                autoComplete="current-password"
              />
            </div>

            <div style={{ textAlign: "right", marginBottom: "1.5rem" }}>
              <Link
                to="/portal/forgot-password"
                style={{ fontSize: "0.8rem", color: "#0353a4", textDecoration: "none" }}
              >
                Olvidaste tu contrasena?
              </Link>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              type="button"
              style={{
                width: "100%",
                padding: "0.8rem",
                fontSize: "0.95rem",
                fontWeight: 600,
                color: "#fff",
                background: loading
                  ? "#64748b"
                  : "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
                border: "none",
                borderRadius: "0.625rem",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                boxShadow: "0 2px 8px rgba(15,23,42,0.25)",
                letterSpacing: "0.01em",
              }}
            >
              {loading ? "Ingresando..." : "Ingresar"}
            </button>
          </form>

          <p
            style={{
              textAlign: "center",
              fontSize: "0.85rem",
              color: "#64748b",
              marginTop: "1.5rem",
            }}
          >
            No tienes cuenta?{" "}
            <Link
              to="/portal/register"
              style={{ color: "#0353a4", textDecoration: "none", fontWeight: 600 }}
            >
              Registrate
            </Link>
          </p>

          <p
            style={{
              textAlign: "center",
              fontSize: "0.75rem",
              color: "#94a3b8",
              marginTop: "2rem",
            }}
          >
            Profit Accounting by MOCCA
          </p>
        </div>
      </div>
    </div>
  );
}
